import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  Alert,
  Button,
  Form,
  OverlayTrigger,
  Spinner,
  Tooltip,
} from "react-bootstrap"
import { toast } from "react-toastify"
import { admin } from "../../config/apiEndpoints"
import axiosProvider from "../../lib/AxiosProvider"
import { loadRazorpayCheckout } from "../../lib/RazorpayCheckout"
import ModalComponent from "../../components/Modal"

const AMOUNT_PATTERN = /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/

const validateAmount = (value) => {
  const normalizedAmount = value.trim()

  if (!normalizedAmount) return "Enter an amount"
  if (!AMOUNT_PATTERN.test(normalizedAmount)) {
    return "Enter a valid INR amount with no more than two decimal places"
  }
  if (!/[1-9]/.test(normalizedAmount)) {
    return "Amount must be greater than zero"
  }

  return ""
}

const apiErrorMessage = (response, fallbackMessage) =>
  response?.response?.data?.message ||
  response?.data?.message ||
  response?.message ||
  fallbackMessage

const formatMinorUnits = (value) => {
  try {
    const amount = window.BigInt(value ?? "0")
    const sign = amount < 0n ? "-" : ""
    const absoluteAmount = amount < 0n ? -amount : amount
    const rupees = (absoluteAmount / 100n)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    const paise = (absoluteAmount % 100n).toString().padStart(2, "0")
    return `${sign}₹${rupees}.${paise}`
  } catch {
    return "₹0.00"
  }
}

const VendorWalletCard = ({ onMinimumBalanceChange, onWalletUpdate }) => {
  const [showAddFunds, setShowAddFunds] = useState(false)
  const [amount, setAmount] = useState("")
  const [amountError, setAmountError] = useState("")
  const [apiError, setApiError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false)
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [createdOrder, setCreatedOrder] = useState(null)
  const [checkoutPayment, setCheckoutPayment] = useState(null)
  const [verificationResult, setVerificationResult] = useState(null)
  const [flowMessage, setFlowMessage] = useState(null)
  const [wallet, setWallet] = useState(null)
  const [walletLoading, setWalletLoading] = useState(true)
  const [walletError, setWalletError] = useState("")
  const paymentFlowInProgress = useRef(false)
  const verificationInProgress = useRef(false)
  const isMounted = useRef(true)

  const refreshWalletBalance = useCallback(async () => {
    setWalletLoading(true)
    setWalletError("")

    try {
      const walletResponse = await axiosProvider({
        method: "GET",
        endpoint: admin.fleetVendorWallet,
      })

      if (!isMounted.current) return

      if (walletResponse?.data?.code !== 200) {
        throw new Error(
          apiErrorMessage(walletResponse, "Unable to load wallet balance")
        )
      }

      setWallet(walletResponse.data.data)
      onMinimumBalanceChange?.(walletResponse.data.data.minimum_balance ?? null)
    } catch (error) {
      if (isMounted.current) {
        setWalletError(error?.message || "Unable to load wallet information")
      }
    } finally {
      if (isMounted.current) setWalletLoading(false)
    }
  }, [onMinimumBalanceChange])

  useEffect(() => {
    isMounted.current = true
    refreshWalletBalance()

    return () => { isMounted.current = false }
  }, [refreshWalletBalance])

  useEffect(() => {
    if (!flowMessage?.text) return

    const toastId = "vendor-wallet-payment-flow"
    const typeByVariant = {
      danger: "error",
      warning: "warning",
      success: "success",
      info: "info",
      secondary: "info",
    }
    const toastOptions = {
      toastId,
      type: typeByVariant[flowMessage.variant] || "default",
      autoClose: flowMessage.variant === "info" ? 4000 : 7000,
    }

    if (toast.isActive(toastId)) {
      toast.update(toastId, {
        ...toastOptions,
        render: flowMessage.text,
      })
      return
    }

    toast(flowMessage.text, toastOptions)
  }, [flowMessage])

  const refreshWalletData = useCallback(async () => {
    await refreshWalletBalance()
    onWalletUpdate?.()
  }, [onWalletUpdate, refreshWalletBalance])

  const closeDialog = () => {
    if (isSubmitting) return
    setShowAddFunds(false)
    setAmountError("")
    setApiError("")
  }

  const openDialog = () => {
    if (paymentFlowInProgress.current) return
    setAmount("")
    setAmountError("")
    setApiError("")
    setShowAddFunds(true)
  }

  const releasePaymentFlow = () => {
    paymentFlowInProgress.current = false
    verificationInProgress.current = false
    setIsCheckoutLoading(false)
    setIsCheckoutOpen(false)
    setIsVerifying(false)
  }

  const verifyCheckoutPayment = async (paymentResponse) => {
    if (verificationInProgress.current) return
    verificationInProgress.current = true
    setCheckoutPayment(paymentResponse)
    setIsCheckoutOpen(false)
    setIsVerifying(true)
    setFlowMessage({
      variant: "info",
      text: "Payment received. Verifying it securely with the server…",
    })

    try {
      const response = await axiosProvider({
        method: "POST",
        endpoint: admin.fleetVendorWalletVerifyPayment,
        data: {
          razorpay_order_id: paymentResponse.razorpay_order_id,
          razorpay_payment_id: paymentResponse.razorpay_payment_id,
          razorpay_signature: paymentResponse.razorpay_signature,
        },
      })
      const responseData = response?.data

      if (
        responseData?.code !== 200 ||
        responseData?.data?.status !== "COMPLETED"
      ) {
        throw new Error(
          apiErrorMessage(response, "Unable to verify the wallet payment")
        )
      }

      setVerificationResult(responseData.data)
      setFlowMessage(null)
      await refreshWalletData()
      toast.success("Wallet topped up successfully.")
    } catch (error) {
      setFlowMessage({
        variant: "danger",
        text: `${error?.message || "Payment verification failed"} The webhook may still confirm the wallet credit.`,
      })
      void refreshWalletData()
    } finally {
      releasePaymentFlow()
    }
  }

  const cancelDismissedCheckout = async (order, paymentAttemptFailed) => {
    setIsCheckoutOpen(false)
    setIsVerifying(true)
    setFlowMessage({
      variant: "info",
      text: "Confirming the payment status before cancelling…",
    })

    try {
      const response = await axiosProvider({
        method: "POST",
        endpoint: admin.fleetVendorWalletCancelPayment,
        data: { razorpay_order_id: order.order_id },
      })
      const responseData = response?.data
      const status = responseData?.data?.status

      if (responseData?.code !== 200 || !status) {
        throw new Error(
          apiErrorMessage(response, "Unable to confirm the payment status")
        )
      }

      if (status === "COMPLETED") {
        setCreatedOrder(null)
        setVerificationResult(responseData.data)
        setFlowMessage(null)
        await refreshWalletData()
        toast.success("Wallet topped up successfully.")
        return
      }

      if (status === "CANCELLED") {
        setCreatedOrder(null)
        setCheckoutPayment(null)
        setFlowMessage({
          variant: "secondary",
          text: "Payment was cancelled before any payment attempt was made.",
        })
        await refreshWalletData()
        return
      }

      setFlowMessage({
        variant: paymentAttemptFailed ? "danger" : "warning",
        text: paymentAttemptFailed
          ? "The payment attempt was unsuccessful, but the order remains pending because another outcome may still be possible."
          : "Checkout was closed, but the payment outcome is not safe to cancel yet. The order remains pending.",
      })
      await refreshWalletData()
    } catch (error) {
      setFlowMessage({
        variant: "warning",
        text: `${error?.message || "Unable to confirm the payment status"}. The order remains pending.`,
      })
      void refreshWalletData()
    } finally {
      releasePaymentFlow()
    }
  }

  const launchCheckout = async (order) => {
    setIsCheckoutLoading(true)
    setFlowMessage({
      variant: "info",
      text: "Loading Razorpay Checkout…",
    })

    try {
      const Razorpay = await loadRazorpayCheckout()
      let checkoutCompleted = false
      let paymentAttemptFailed = false

      const checkout = new Razorpay({
        key: order.key_id,
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency,
        name: "MegaCharge",
        description: "Fleet Vendor wallet top-up",
        handler: (response) => {
          checkoutCompleted = true
          verifyCheckoutPayment(response)
        },
        modal: {
          confirm_close: true,
          ondismiss: () => {
            if (checkoutCompleted) return
            void cancelDismissedCheckout(order, paymentAttemptFailed)
          },
        },
      })

      checkout.on("payment.failed", (response) => {
        paymentAttemptFailed = true
        setFlowMessage({
          variant: "danger",
          text:
            response?.error?.description ||
            "Payment failed. Retry in Checkout or close it to cancel.",
        })
      })

      setIsCheckoutLoading(false)
      setIsCheckoutOpen(true)
      setFlowMessage({
        variant: "info",
        text: "Razorpay Checkout is open. Complete or cancel the payment there.",
      })
      checkout.open()
    } catch (error) {
      setFlowMessage({
        variant: "danger",
        text: error?.message || "Unable to open Razorpay Checkout",
      })
      releasePaymentFlow()
    }
  }

  const retryCheckout = () => {
    if (!createdOrder || paymentFlowInProgress.current) return
    paymentFlowInProgress.current = true
    setCheckoutPayment(null)
    setVerificationResult(null)
    launchCheckout(createdOrder)
  }

  const retryVerification = () => {
    if (!checkoutPayment || paymentFlowInProgress.current) return
    paymentFlowInProgress.current = true
    verifyCheckoutPayment(checkoutPayment)
  }

  const createTopUpOrder = async (event) => {
    event?.preventDefault()
    if (paymentFlowInProgress.current) return

    const normalizedAmount = amount.trim()
    const validationError = validateAmount(normalizedAmount)
    if (validationError) {
      setAmountError(validationError)
      return
    }

    paymentFlowInProgress.current = true
    setIsSubmitting(true)
    setAmountError("")
    setApiError("")
    setCreatedOrder(null)
    setCheckoutPayment(null)
    setVerificationResult(null)
    setFlowMessage(null)

    try {
      const response = await axiosProvider({
        method: "POST",
        endpoint: admin.fleetVendorWalletTopUpOrder,
        data: { amount: normalizedAmount },
      })
      const responseData = response?.data

      if (
        responseData?.code !== 201 ||
        !responseData?.data?.order_id ||
        responseData?.data?.amount === undefined ||
        !responseData?.data?.currency ||
        !responseData?.data?.key_id
      ) {
        throw new Error(
          apiErrorMessage(response, "Unable to create wallet top-up order")
        )
      }

      setCreatedOrder(responseData.data)
      setShowAddFunds(false)
      setAmount("")
      setIsSubmitting(false)
      await launchCheckout(responseData.data)
    } catch (error) {
      setApiError(error?.message || "Unable to create wallet top-up order")
      paymentFlowInProgress.current = false
    } finally {
      setIsSubmitting(false)
    }
  }

  const isPaymentBusy =
    isSubmitting || isCheckoutLoading || isCheckoutOpen || isVerifying
  const canRetryVerification =
    !isPaymentBusy && checkoutPayment && !verificationResult
  const canReopenCheckout =
    !isPaymentBusy && createdOrder && !checkoutPayment
  const walletActionLabel = canRetryVerification
    ? "Retry payment verification"
    : canReopenCheckout
      ? "Reopen Razorpay Checkout"
      : "Add funds"

  const handleWalletAction = () => {
    if (canRetryVerification) {
      retryVerification()
      return
    }
    if (canReopenCheckout) {
      retryCheckout()
      return
    }
    openDialog()
  }

  return (
    <>
      <div className="bg_light_orange details_card">
        <div className="card_titleicon d-flex align-items-start justify-content-between gap-2">
          <h3 className="card_title">Fleet Vendor Wallet</h3>
          <OverlayTrigger
            placement="top"
            trigger={["hover", "focus"]}
            overlay={
              <Tooltip id="wallet-add-funds-tooltip">
                {walletActionLabel}
              </Tooltip>
            }
          >
            <Button
              type="button"
              variant="primary"
              className="border-0 rounded-circle flex-shrink-0 d-inline-flex align-items-center justify-content-center p-0"
              style={{ width: "2rem", height: "2rem" }}
              aria-label={walletActionLabel}
              onClick={handleWalletAction}
              disabled={isPaymentBusy}
            >
              <i className="icon_main plus_black" aria-hidden="true"></i>
            </Button>
          </OverlayTrigger>
        </div>

        {walletLoading ? (
          <p className="tls_numbers pe-5">
            <Spinner size="sm" animation="border" className="me-2" />
            Loading...
          </p>
        ) : (
          <p className="tls_numbers pe-5">
            {formatMinorUnits(wallet?.balance_minor)}
          </p>
        )}
        {wallet?.fleet_vendor?.cost_per_kwh !== undefined &&
          wallet?.fleet_vendor?.cost_per_kwh !== null && (
            <p className="text-muted small mt-1 mb-0">
              Rate: ₹{wallet.fleet_vendor.cost_per_kwh}/kWh
            </p>
          )}
        {walletError && (
          <Alert variant="danger" className="mt-3 mb-0 py-2 px-2 small">
            <div className="d-flex align-items-center justify-content-between gap-2">
              <span>{walletError}</span>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={refreshWalletBalance}
              >
                Retry
              </Button>
            </div>
          </Alert>
        )}

      </div>

      <ModalComponent
        show={showAddFunds}
        onHide={closeDialog}
        modeltitle="Add Wallet Funds"
        modalsize="md"
        backdrop="static"
        keyboard={!isSubmitting}
        btnclosetext="Cancel"
        submitname={
          isSubmitting ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              Creating Order
            </>
          ) : (
            "Continue to Checkout"
          )
        }
        handleSubmit={createTopUpOrder}
        formbuttonid="vendor-wallet-top-up-form"
        loading={isSubmitting}
      >
        <Form
          id="vendor-wallet-top-up-form"
          noValidate
          onSubmit={(event) => event.preventDefault()}
        >
          <Form.Group controlId="vendorWalletTopUpAmount">
            <Form.Label>
              Amount (INR) <span className="required_star">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="123.45"
              value={amount}
              disabled={isSubmitting}
              isInvalid={Boolean(amountError)}
              onChange={(event) => {
                setAmount(event.target.value)
                setAmountError("")
                setApiError("")
              }}
            />
            <Form.Text className="text-body-secondary">
              Enter rupees using up to two decimal places.
            </Form.Text>
            <Form.Control.Feedback type="invalid">
              {amountError}
            </Form.Control.Feedback>
          </Form.Group>

          {apiError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {apiError}
            </Alert>
          )}
        </Form>
      </ModalComponent>
    </>
  )
}

export default VendorWalletCard
